import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { db, auth } from '../firebase/config';
import { doc, getDoc, collection, query, where, orderBy, getDocs, updateDoc, serverTimestamp, addDoc, Timestamp, deleteDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { showError } from '../utils/notifications';
import { logBalanceRecharge } from '../services/auditService';
import Sidebar from '../components/Sidebar';
import MobileHeader from '../components/MobileHeader';
import { useStableCallback } from '../hooks/useOptimizedCallback';

function VendorDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rechargeAmount, setRechargeAmount] = useState('');
  const [recharges, setRecharges] = useState([]);
  const [ticketsSold, setTicketsSold] = useState([]);
  const [ticketsStats, setTicketsStats] = useState({
    last30Days: 0,
    totalRevenue: 0,
    motoTickets: 0,
    autoTickets: 0
  });
  const [activeSection, setActiveSection] = useState('vendors');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // State for modal dialogs
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'confirm'
  });
  
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'alert'
  });

  // Format date helper function
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    try {
      let date;
      if (typeof timestamp === 'object' && timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else {
        return String(timestamp);
      }
      
      return date.toLocaleDateString() + ', ' + date.toLocaleTimeString();
    } catch (error) {
      return 'Fecha inválida';
    }
  };  const fetchVendorData = useStableCallback(async () => {
    try {
      console.log(`Intentando cargar CA con ID: ${id}`);
      
      // Verificar si el ID está presente
      if (!id) {
        console.error('ID del CA no proporcionado');
        showError('Error', 'No se proporcionó identificación del controlador');
        navigate('/vendors');
        return;
      }

      // Try to fetch from 'usuarios' collection first (where CA vendors are stored)
      const vendorRef = doc(db, 'usuarios', id);
      const vendorSnap = await getDoc(vendorRef);
      
      if (vendorSnap.exists()) {
        console.log(`CA encontrado en la colección usuarios: ${id}`);
        const vendorData = vendorSnap.data();
        
        // Check if this is actually a CA (vendor)
        if (vendorData.rol === 'CA') {
          setVendor({ 
            id: vendorSnap.id, 
            ...vendorData,
            name: vendorData.nombre,
            numberCa: vendorData.numberCa || 'N/A',
            balance: vendorData.balance || 0,
            status: vendorData.status === 1 ? 'active' : 'inactive',
            ticketsMoto: vendorData.ticketsMoto || 0,
            ticketsAuto: vendorData.ticketsAuto || 0,
            identificationNumber: vendorData.cedula || 'N/A',
            telefono: vendorData.telefono || 'N/A',
            direccion: vendorData.direccion || vendorData.address || 'dirección sin dirección',
            zone: vendorData.zona ||  vendorData.zone || 'N/A',
            email: vendorData.email || vendorSnap.id
          });
        } else {
          console.warn(`El usuario ${id} no es un CA (rol: ${vendorData.rol})`);
          showError('Acceso denegado', 'Usuario no es un Controlador Autorizado');
          navigate('/vendors');
        }
      } else {
        console.warn(`CA no encontrado con ID: ${id}, intentando búsqueda alternativa...`);
        
        // Probar búsqueda por correo electrónico en campo email
        try {
          const usuariosRef = collection(db, 'usuarios');
          const qEmail = query(usuariosRef, where("email", "==", id), where("rol", "==", "CA"));
          const emailQuerySnapshot = await getDocs(qEmail);
          
          if (!emailQuerySnapshot.empty) {
            // Se encontró por correo electrónico
            const docSnap = emailQuerySnapshot.docs[0];
            const vendorData = docSnap.data();
            console.log(`CA encontrado por campo email: ${docSnap.id}`);
            
            setVendor({ 
              id: docSnap.id, 
              ...vendorData,
              name: vendorData.nombre,
              numberCa: vendorData.numberCa || 'N/',
              balance: vendorData.balance || 0,
              status: vendorData.status === 1 ? 'active' : 'inactive',
              ticketsMoto: vendorData.ticketsMoto || 0,
              ticketsAuto: vendorData.ticketsAuto || 0,
              identificationNumber: vendorData.cedula || 'N/A',
              telefono: vendorData.telefono || 'N/A',
              direccion: vendorData.direccion || vendorData.address || 'dirección sin dirección',
              zone: vendorData.zona || vendorData.zone || 'N/A',
              email: vendorData.email || docSnap.id
            });
            return;
          }
          
          // If not found in usuarios, try the vendors collection as fallback
          const fallbackVendorRef = doc(db, 'vendors', id);
          const fallbackVendorSnap = await getDoc(fallbackVendorRef);
          
          if (fallbackVendorSnap.exists()) {
            console.log(`CA encontrado en colección vendors: ${id}`);
            setVendor({ id: fallbackVendorSnap.id, ...fallbackVendorSnap.data() });
          } else {
            console.error(`CA no encontrado en ninguna colección: ${id}`);
            showError('No encontrado', 'Controlador Autorizado no disponible');
            navigate('/vendors');
          }
        } catch (fallbackError) {
          console.error('Error en búsquedas alternativas:', fallbackError);
          showError('Error', 'Error al buscar información del controlador');
          navigate('/vendors');
        }
      }
    } catch (error) {
      console.error('Error al obtener datos del vendedor:', error);
      showError('Error', 'Error al cargar datos del controlador');
      navigate('/vendors');
    }
  });

  const fetchRechargeHistory = useStableCallback(async () => {
    try {
      const rechargesRef = collection(db, 'RechargeInternas');
      const q = query(
        rechargesRef,
        where("userId", "==", id),
        orderBy("date", "desc")
      );
      const querySnapshot = await getDocs(q);
      
      const rechargesList = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        rechargesList.push({ 
          id: doc.id, 
          ...data,
          timestamp: formatTimestamp(data.timestamp),
          rawDate: data.timestamp || data.date // Keep raw date for filtering
        });
      });
      
      setRecharges(rechargesList);
    } catch (error) {
      console.error('Error al obtener historial de recargas:', error);
    }
  });

  const fetchTicketsSold = useStableCallback(async () => {
    try {
      if (!vendor) {
        console.log('No hay datos del vendor aún');
        return;
      }
      
      console.log('=== DEBUG TICKETS ===');
      console.log('Vendor data:', vendor);
      console.log('Vendor email:', vendor.email);
      console.log('Vendor ID:', vendor.id);
      
      // Calcular fecha de hace 30 días
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      console.log('Buscando tickets desde:', thirtyDaysAgo);
      
      const ticketsRef = collection(db, 'tickets');
      
      // Primero intentar con el email del vendor
      let q = query(
        ticketsRef,
        where("vendor", "==", vendor.email),
        where("createdAt", ">=", Timestamp.fromDate(thirtyDaysAgo)),
        orderBy("createdAt", "desc")
      );
      
      console.log('Consultando con vendor.email:', vendor.email);
      let querySnapshot = await getDocs(q);
      console.log('Resultados con email:', querySnapshot.size);
      
      // Si no encuentra con email, intentar con el ID del documento
      if (querySnapshot.empty && vendor.id !== vendor.email) {
        console.log('Intentando con vendor.id:', vendor.id);
        q = query(
          ticketsRef,
          where("vendor", "==", vendor.id),
          where("createdAt", ">=", Timestamp.fromDate(thirtyDaysAgo)),
          orderBy("createdAt", "desc")
        );
        querySnapshot = await getDocs(q);
        console.log('Resultados con ID:', querySnapshot.size);
      }
      
      // Si aún no encuentra, intentar sin filtro de fecha para ver si hay tickets del vendor
      if (querySnapshot.empty) {
        console.log('Buscando tickets sin filtro de fecha...');
        q = query(
          ticketsRef,
          where("vendor", "==", vendor.email),
          orderBy("createdAt", "desc")
        );
        const allTicketsSnapshot = await getDocs(q);
        console.log('Total tickets del vendor (sin filtro fecha):', allTicketsSnapshot.size);
        
        if (allTicketsSnapshot.size > 0) {
          console.log('Primer ticket encontrado:', allTicketsSnapshot.docs[0].data());
        }
        
        // Si no encuentra con email, probar con ID
        if (allTicketsSnapshot.empty && vendor.id !== vendor.email) {
          q = query(
            ticketsRef,
            where("vendor", "==", vendor.id),
            orderBy("createdAt", "desc")
          );
          const allTicketsSnapshotById = await getDocs(q);
          console.log('Total tickets del vendor por ID (sin filtro fecha):', allTicketsSnapshotById.size);
        }
        
        // Si no encuentra con ID, probar con numberCa
        if (allTicketsSnapshot.empty && vendor.numberCa) {
          console.log('Intentando con numberCa:', vendor.numberCa);
          q = query(
            ticketsRef,
            where("numberCa", "==", vendor.numberCa),
            orderBy("createdAt", "desc")
          );
          const allTicketsSnapshotByCA = await getDocs(q);
          console.log('Total tickets del vendor por numberCa (sin filtro fecha):', allTicketsSnapshotByCA.size);
          
          if (allTicketsSnapshotByCA.size > 0) {
            console.log('Primer ticket por CA encontrado:', allTicketsSnapshotByCA.docs[0].data());
            
            // Si encuentra tickets por numberCa, hacer la consulta con filtro de fecha
            q = query(
              ticketsRef,
              where("numberCa", "==", vendor.numberCa),
              where("createdAt", ">=", Timestamp.fromDate(thirtyDaysAgo)),
              orderBy("createdAt", "desc")
            );
            querySnapshot = await getDocs(q);
            console.log('Resultados con numberCa (últimos 30 días):', querySnapshot.size);
          }
        }
      }
      
      const ticketsList = [];
      let totalRevenue = 0;
      let motoCount = 0;
      let autoCount = 0;
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('Ticket encontrado:', {
          id: doc.id,
          vendor: data.vendor,
          vehicleType: data.vehicleType,
          price: data.price,
          createdAt: data.createdAt
        });
        
        const ticket = {
          id: doc.id,
          ...data,
          formattedDate: formatTimestamp(data.createdAt)
        };
        
        ticketsList.push(ticket);
        totalRevenue += data.price || 0;
        
        if (data.vehicleType === 'Motocicleta' || data.vehicleType === 'moto') {
          motoCount++;
        } else if (data.vehicleType === 'Automovil' || data.vehicleType === 'auto') {
          autoCount++;
        }
      });
      
      console.log('Estadísticas finales:', {
        totalTickets: ticketsList.length,
        totalRevenue,
        motoCount,
        autoCount
      });
      
      setTicketsSold(ticketsList);
      setTicketsStats({
        last30Days: ticketsList.length,
        totalRevenue: totalRevenue,
        motoTickets: motoCount,
        autoTickets: autoCount
      });
      
      console.log(`✅ Tickets procesados para ${vendor.email}: ${ticketsList.length} en los últimos 30 días`);
    } catch (error) {
      console.error('❌ Error al obtener tickets vendidos:', error);
    }
  });

  // Filter today's recharges for summary
  const getTodayRecharges = () => {
    const today = new Date();
    const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
    
    return recharges.filter(recharge => {
      let rechargeDate;
      
      try {
        if (recharge.rawDate && typeof recharge.rawDate === 'object' && recharge.rawDate.seconds) {
          rechargeDate = new Date(recharge.rawDate.seconds * 1000);
        } else if (recharge.rawDate instanceof Date) {
          rechargeDate = recharge.rawDate;
        } else if (recharge.rawDate) {
          rechargeDate = new Date(recharge.rawDate);
        } else {
          return false;
        }
        
        return rechargeDate >= startOfDay && rechargeDate <= endOfDay;
      } catch (error) {
        console.error('Error parsing date:', error);
        return false;
      }
    });
  };

  // Calculate today's summary
  const todayRecharges = getTodayRecharges();
  const todayStats = {
    totalAmount: todayRecharges.reduce((sum, recharge) => sum + (recharge.amount || 0), 0),
    totalTickets: todayRecharges.reduce((sum, recharge) => sum + (recharge.ticketCount || 0), 0),
    motosAmount: todayRecharges
      .filter(r => r.type === 'motos' || r.type === 'Tickets')
      .reduce((sum, recharge) => sum + (recharge.amount || 0), 0),
    autosAmount: todayRecharges
      .filter(r => r.type === 'autos')
      .reduce((sum, recharge) => sum + (recharge.amount || 0), 0)
  };

  // Effect to fetch tickets when vendor data is available
  useEffect(() => {
    if (vendor && vendor.email) {
      fetchTicketsSold();
    }
  }, [vendor, fetchTicketsSold]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          // Try multiple lookup strategies for user verification
          let userDoc = null;
          let userData = null;
          
          // Strategy 1: Look up by email
          try {
            const userDocRef = doc(db, 'usuarios', currentUser.email);
            userDoc = await getDoc(userDocRef);
            if (userDoc.exists()) {
              userData = userDoc.data();
            }
          } catch (error) {
            console.log("Lookup by email failed:", error);
          }
          
          // Strategy 2: Try lookup by UID
          if (!userData) {
            try {
              const userDocRefByUID = doc(db, 'usuarios', currentUser.uid);
              userDoc = await getDoc(userDocRefByUID);
              if (userDoc.exists()) {
                userData = userDoc.data();
              }
            } catch (error) {
              console.log("Lookup by UID failed:", error);
            }
          }
          
          // Strategy 3: Query by email field
          if (!userData) {
            try {
              const usuariosRef = collection(db, 'usuarios');
              const q = query(usuariosRef, where('email', '==', currentUser.email));
              const querySnapshot = await getDocs(q);
              
              if (!querySnapshot.empty) {
                querySnapshot.forEach((doc) => {
                  userData = doc.data();
                });
              }
            } catch (error) {
              console.log("Email field query failed:", error);
            }
          }
          
          if (!userData) {
            showError("Usuario no encontrado", "Tu perfil no existe en el sistema");
            auth.signOut();
            navigate('/login');
            return;
          }
          
          setUser({
            ...currentUser,
            rol: userData.rol,
            nombre: userData.nombre
          });
          
          setUserRole(userData.rol);
          
          // Verificar si el usuario tiene permiso para acceder a esta página
          if (userData.rol === 'ADMIN' || userData.rol === 'CA' || userData.rol === 'Admin' || userData.rol === 'Auditor') {
            fetchVendorData();
            fetchRechargeHistory();
            fetchTicketsSold();
          } else {
            showError("Acceso denegado", "No tienes permisos para acceder a esta página");
            navigate('/dashboard');
          }
        } catch (error) {
          console.error("Error al verificar rol de usuario:", error);
          showError("Error de autenticación", "Hubo un problema al verificar tus credenciales");
        }
      } else {
        navigate('/login');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [id, navigate, fetchVendorData, fetchRechargeHistory, fetchTicketsSold]);

  // ✅ FUNCIÓN PARA VALIDAR RECARGA DE SALDO CON API EXTERNA
  const validateBalanceRechargeWithAPI = async (rechargeData) => {
    console.log(`🔒 INICIANDO VALIDACIÓN API EXTERNA PARA SALDO (OBLIGATORIA)`);
    console.log(`🌐 Enviando validación de saldo a API externa:`, rechargeData);
    
    try {
      // Crear FormData según el formato del curl proporcionado
      const formData = new FormData();
      formData.append('documentId', rechargeData.documentId);
      formData.append('adminEmail', rechargeData.adminEmail);
      formData.append('createdAt', rechargeData.createdAt);
      formData.append('description', rechargeData.description);
      formData.append('ipAddress', rechargeData.ipAddress);
      formData.append('numberCa', rechargeData.numberCa);
      formData.append('operationType', rechargeData.operationType);
      formData.append('vehicleType', rechargeData.vehicleType);
      formData.append('status', rechargeData.status);
      formData.append('platform', rechargeData.platform);
      formData.append('price', rechargeData.price);
      formData.append('ticketsAdded', rechargeData.ticketsAdded);
      formData.append('userName', rechargeData.userName);
      formData.append('vendorId', rechargeData.vendorId);
      formData.append('tokenticked', rechargeData.tokenticked);

      // 🌐 URL USANDO PROXY DE VERCEL - SIN PATH ADICIONAL PARA LLEGAR A RAÍZ API
      const apiUrl = '/api_sert/';
      
      console.log(`🌐 Enviando a: ${apiUrl} (modo: ${process.env.NODE_ENV})`);

      const response = await fetch(apiUrl, {
        method: 'POST',
        mode: 'cors', // Agregar modo CORS explícito
        headers: {
          'Authorization': 'Bearer 9dsKHD0gjU98WX6fklBTi1eZHxUoSm44M3v8aQB3AsiE8xCD1AydSP'
        },
        body: formData
      });

      // 📋 LOGGING DETALLADO SIEMPRE - PARA VER QUE RESPONDE LA API (SALDO)
      console.log(`📡 [SALDO] RESPUESTA API STATUS: ${response.status}`);
      console.log(`📡 [SALDO] RESPUESTA API STATUS TEXT: ${response.statusText}`);
      console.log(`📡 [SALDO] RESPUESTA API OK: ${response.ok}`);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ API response error for balance: ${response.status} - ${errorText}`);
        console.error(`📋 [SALDO] RESPUESTA COMPLETA API (ERROR):`, {
          status: response.status,
          statusText: response.statusText,
          ok: response.ok,
          errorText: errorText
        });
        console.error(`🚫 VALIDACIÓN API PARA SALDO FALLÓ - NO SE DEBE PROCESAR NADA EN FIREBASE`);
        return {
          success: false,
          message: `Error ${response.status}: ${errorText || 'Error en validación API de saldo'}`
        };
      }

      const result = await response.json();
      
      // 📋 LOGGING DETALLADO SIEMPRE - RESPUESTA JSON COMPLETA (SALDO)
      console.log(`📡 ===== RESPUESTA COMPLETA DE LA API EXTERNA (SALDO) =====`);
      console.log(`📡 [SALDO] RESPONSE STATUS: ${response.status} (${response.statusText})`);
      console.log(`📡 [SALDO] RESPONSE JSON:`, result);
      console.log(`📡 [SALDO] MENSAJE: ${result?.Mensaje || 'No disponible'}`);
      console.log(`📡 [SALDO] ERROR CODE: ${result?.error_code || 'No disponible'}`);
      console.log(`📡 [SALDO] RESPUESTA COMPLETA:`, JSON.stringify(result, null, 2));
      console.log(`📡 ===================================================`);
      
      // ✅ VALIDACIÓN ESPECÍFICA DEL FORMATO DE RESPUESTA REQUERIDO
      const isValidResponse = result && 
                              result.Mensaje === "Transacción Exitosa" && 
                              result.error_code === "E001";
      
      if (!isValidResponse) {
        console.error(`❌ API de saldo respondió pero formato incorrecto:`, result);
        console.error(`🚫 SE ESPERABA: {"Mensaje":"Transacción Exitosa","error_code":"E001"}`);
        console.error(`🚫 SE RECIBIÓ:`, JSON.stringify(result));
        console.error(`🚫 VALIDACIÓN API DE SALDO FALLÓ POR FORMATO INCORRECTO`);
        
        return {
          success: false,
          message: `Formato de respuesta API de saldo incorrecto. Esperado: {"Mensaje":"Transacción Exitosa","error_code":"E001"}, Recibido: ${JSON.stringify(result)}`
        };
      }
      
      console.log(`✅ VALIDACIÓN API PARA SALDO EXITOSA - SE PUEDE PROCEDER CON FIREBASE`);
      console.log(`✅ Respuesta API de saldo válida: ${result.Mensaje} (${result.error_code})`);
      
      return {
        success: true,
        message: result.Mensaje || 'Validación API de saldo exitosa',
        data: result
      };

    } catch (error) {
      console.error(`❌ Error conectando con API externa para saldo:`, error);
      console.error(`🚫 FALLO CRÍTICO: API externa no respondió para saldo - BLOQUEANDO COMPLETAMENTE la operación`);
      console.error(`🔒 SEGURIDAD: NO se realizará NINGUNA actualización en Firebase para saldo`);
      
      return {
        success: false,
        message: `Error de conexión API: ${error.message}`
      };
    }
  };

  const handleRecharge = async (e) => {
    e.preventDefault();
    
    // Validate recharge amount
    if (!rechargeAmount || isNaN(rechargeAmount) || parseFloat(rechargeAmount) <= 0) {
      showError("Monto Inválido", "Por favor ingrese un monto de recarga válido");
      return;
    }
    
    try {
      // Confirm recharge with modal
      setConfirmModal({
        isOpen: true,
        title: "Confirmar Recarga",
        message: `¿Está seguro que desea recargar $${parseFloat(rechargeAmount).toFixed(2)} a ${vendor.nombre}?`,
        type: "confirm",
        onConfirm: async () => {
          setConfirmModal(prev => ({...prev, isOpen: false}));
          
          try {
            setLoading(true);
            
            // Obtener datos actuales del CA
            const currentBalance = vendor.balance || 0;
            const newBalance = currentBalance + Number(rechargeAmount);
            
            // Obtener la IP del cliente
            let clientIP = 'No disponible';
            try {
              const response = await fetch('https://api.ipify.org?format=json', { timeout: 2000 });
              if (response.ok) {
                const data = await response.json();
                clientIP = data.ip;
              }
            } catch (ipError) {
              console.log("No se pudo obtener IP externa:", ipError.message);
              clientIP = 'Panel Admin (IP local)';
            }
            
            // Formatear fecha en español
            const timestamp = Timestamp.now();
            const currentDate = new Date();
            const fechaStr = currentDate.toLocaleDateString('es-ES', {
              day: '2-digit',
              month: 'long',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              hour12: true,
              timeZoneName: 'longOffset'
            });
            
            // ✅ PASO 1: VALIDACIÓN API EXTERNA OBLIGATORIA
            console.log(`🔒 PASO 1: VALIDACIÓN API EXTERNA OBLIGATORIA PARA SALDO`);
            console.log(`🔄 Validando recarga de saldo en API externa antes de procesar...`);
            
            const apiValidationResult = await validateBalanceRechargeWithAPI({
              documentId: `balance_recharge_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              adminEmail: user?.email || 'Sistema',
              createdAt: fechaStr,
              description: `Recarga de saldo: $${Number(rechargeAmount).toFixed(2)}`,
              ipAddress: clientIP,
              numberCa: vendor.numberCa || 'N/A',
              operationType: 'recarga_saldo',
              vehicleType: 'balance', // Para recargas de saldo
              status: 'completed',
              platform: 'web_admin',
              price: Number(rechargeAmount).toString(),
              ticketsAdded: '0', // No son tickets, es saldo
              userName: vendor.name || vendor.nombre,
              vendorId: id,
              tokenticked: 'balance_operation' // Operación de saldo
            });

            // ❌ SI LA API FALLA, DETENER COMPLETAMENTE LA OPERACIÓN
            if (!apiValidationResult.success) {
              console.error(`🚫 VALIDACIÓN API PARA SALDO FALLÓ - DETENIENDO OPERACIÓN`);
              console.error(`❌ Razón: ${apiValidationResult.message}`);
              console.error(`🔒 SEGURIDAD: NO se actualizará NADA en Firebase`);
              
              throw new Error(`🚫 RECARGA DE SALDO COMPLETAMENTE BLOQUEADA: ${apiValidationResult.message}\n\n⚠️ CRÍTICO: Sin validación del servidor externo NO se puede procesar la recarga.\n\n🔒 El saldo NO ha sido modificado por seguridad.`);
            }

            console.log(`✅ PASO 1 COMPLETADO: API validación exitosa para saldo - ${apiValidationResult.message}`);
            console.log(`🔒 PASO 2: PROCEDER CON REGISTRO EN RECHARGE (API VALIDADA)`);
            
            // ✅ PASO 2: REGISTRO EN COLECCIÓN RECHARGE
            const rechargeRef = collection(db, 'RechargeInternas');
            let rechargeDocRef;
            
            try {
              rechargeDocRef = await addDoc(rechargeRef, {
                // Datos del CA
                vendorId: id,
                userId: id, // Para compatibilidad
                numberCa: vendor.numberCa || 'N/A',
                userName: vendor.name || vendor.nombre,
                
                // Información personal del CA
                cedulaCA: vendor.identificationNumber || vendor.cedula || 'Sin cédula',
                telefonoCA: vendor.telefono || vendor.phone || 'Sin teléfono',
                direccionCA: vendor.direccion || vendor.address || 'dirección sin dirección',
                
                // Datos de la operación
                operationType: 'recarga_saldo',
                type: 'balance',
                amount: Number(rechargeAmount),
                price: Number(rechargeAmount),
                previousBalance: currentBalance,
                newBalance: newBalance,
                
                // Fechas
                date: timestamp,
                timestamp: timestamp,
                fechaOperacion: timestamp,
                fechaDocumento: fechaStr,
                createdAt: serverTimestamp(),
                
                // Notas y descripción
                notes: document.getElementById('rechargeNotes')?.value || '',
                description: `Recarga de saldo: $${Number(rechargeAmount).toFixed(2)}`,
                
                // Datos del administrador
                adminId: user?.uid || null,
                adminEmail: user?.email || 'Sistema',
                adminName: user?.nombre || user?.displayName || user?.email || 'Sistema',
                executedBy: user?.nombre || user?.displayName || user?.email || 'Sistema',
                executorName: user?.nombre || user?.displayName || user?.email || 'Sistema',
                createdBy: user?.email || 'Sistema',
                
                // IP y plataforma
                clientIP: clientIP,
                ipAddress: clientIP,
                ip: clientIP,
                platform: 'web_admin',
                
                // Estado
                status: 'completed'
              });
              console.log(`✅ PASO 2 COMPLETADO: Recarga registrada con ID: ${rechargeDocRef.id}`);
            } catch (error) {
              console.error(`❌ Error al registrar la recarga de saldo: ${error.message}`);
              throw new Error(`No se pudo registrar la recarga de saldo: ${error.message}`);
            }
            
            console.log(`🔒 PASO 3: ACTUALIZAR BALANCE DEL CA EN FIREBASE`);
            
            // ✅ PASO 3: ACTUALIZAR BALANCE DEL CA
            try {
              const vendorRef = doc(db, 'usuarios', id);
              await updateDoc(vendorRef, {
                balance: newBalance,
                lastRecharge: {
                  amount: Number(rechargeAmount),
                  date: serverTimestamp(),
                  by: user.email || user.uid
                },
                lastModified: serverTimestamp()
              });
              console.log(`✅ PASO 3 COMPLETADO: Balance del CA actualizado en Firebase`);
            } catch (error) {
              console.error(`❌ Error actualizando balance del CA: ${error.message}`);
              // Si no se puede actualizar el CA, revertir el registro de recarga
              try {
                await deleteDoc(doc(db, 'RechargeInternas', rechargeDocRef.id));
                console.log(`🔄 Registro de recarga de saldo revertido por fallo en actualización del CA`);
              } catch (revertError) {
                console.error(`❌ Error al revertir registro de recarga de saldo: ${revertError.message}`);
              }
              throw new Error(`No se pudo actualizar el balance del CA: ${error.message}`);
            }
            
            console.log(`🔒 PASO 4: LOGGING DE AUDITORÍA Y ACTUALIZACIÓN ESTADO LOCAL`);
            
            // ✅ PASO 4: LOGGING DE AUDITORÍA
            await logBalanceRecharge(
              user || { email: 'Unknown' },
              id,
              vendor.nombre || vendor.name || 'Unknown',
              Number(rechargeAmount),
              currentBalance,
              newBalance,
              {
                operationType: 'balance_recharge',
                source: 'vendor_details_page',
                rechargeMethod: 'admin_panel',
                notes: document.getElementById('rechargeNotes')?.value || '',
                vendorRole: 'CA'
              }
            );

            // ✅ PASO 4: ACTUALIZACIÓN ESTADO LOCAL
            setVendor({
              ...vendor,
              balance: newBalance,
              lastRecharge: {
                amount: Number(rechargeAmount),
                date: new Date(),
                by: user.email || user.uid
              }
            });
            
            setRechargeAmount('');
            if (document.getElementById('rechargeNotes')) {
              document.getElementById('rechargeNotes').value = '';
            }
            
            console.log(`✅ RECARGA DE SALDO COMPLETAMENTE EXITOSA: Todos los pasos completados con validación API`);
            
            // Mostrar notificación de éxito
            setAlertModal({
              isOpen: true,
              title: "Recarga Exitosa",
              message: `Se ha recargado $${parseFloat(rechargeAmount).toFixed(2)} al CA ${vendor.nombre}. Nuevo saldo: $${newBalance.toFixed(2)}`,
              type: "success"
            });
            
            // Actualizar el historial de recargas
            fetchRechargeHistory();
          } catch (error) {
            console.error("Error procesando recarga:", error);
            showError("Error", `No se pudo procesar la recarga: ${error.message}`);
          } finally {
            setLoading(false);
          }
        }
      });
    } catch (error) {
      showError("Error", `No se pudo preparar la recarga: ${error.message}`);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100">
        <div className="text-center bg-white p-8 rounded-lg shadow-md">
          <h2 className="text-xl font-bold text-red-500 mb-4">Vendedor no encontrado</h2>
          <p className="mb-4">El vendedor que estás buscando no existe o ha sido eliminado.</p>
          <button
            onClick={() => navigate('/vendors')}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
          >
            Volver a la lista de vendedores
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      {/* Mobile Header */}
      <MobileHeader 
        title="Detalles de Vendedor" 
        userEmail={user?.email}
        userRole={userRole}
        toggleMenu={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      />
      
      {/* Add top spacing for mobile to prevent content from being hidden under header */}
      <div className="md:hidden h-14"></div>
      
      <div className="flex flex-1 overflow-hidden">
        {/* Overlay for mobile menu */}
        {isMobileMenuOpen && (
          <div 
            className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-10"
            onClick={() => setIsMobileMenuOpen(false)}
          ></div>
        )}
        
        {/* Sidebar */}
        <Sidebar 
          activeSection={activeSection} 
          setActiveSection={setActiveSection} 
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
        />

        {/* Main Content */}
        <div className="flex-1 overflow-auto">
          {/* Top Header (desktop only) */}
          <header className="bg-white shadow px-6 py-4 hidden md:block">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <Link to="/vendors" className="mr-2 text-blue-500 hover:text-blue-700">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                </Link>
                <h1 className="text-xl font-bold text-gray-900">Detalles de Vendedor</h1>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user?.email}</p>
                <p className="text-xs text-gray-500">Administrador</p>
              </div>
            </div>
          </header>

          {/* Mobile back button (visible only on mobile) */}
          <div className="px-4 py-2 md:hidden">
            <Link to="/vendors" className="flex items-center text-blue-500">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Volver a la lista
            </Link>
          </div>

          {/* Content */}
          <main className="p-6">
            {/* Today's Summary */}
            <div className="bg-white rounded-lg shadow-md mb-6">
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">
                  Resumen del Día - {new Date().toLocaleDateString('es-ES')}
                </h3>
                <p className="text-sm text-gray-600 mb-4">Solo se muestran las recargas realizadas </p>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="flex items-center mb-2">
                      <span className="text-2xl mr-2">🏍️</span>
                      <span className="text-sm font-medium text-gray-700">Recargas motos hoy</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-600">${todayStats.motosAmount.toFixed(2)}</p>
                    <p className="text-xs text-gray-500">Sus recargas del día</p>
                  </div>
                  
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="flex items-center mb-2">
                      <span className="text-2xl mr-2">🚗</span>
                      <span className="text-sm font-medium text-gray-700">Recargas autos hoy</span>
                    </div>
                    <p className="text-2xl font-bold text-green-600">${todayStats.autosAmount.toFixed(2)}</p>
                    <p className="text-xs text-gray-500">Sus recargas del día</p>
                  </div>
                  
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <div className="flex items-center mb-2">
                      <span className="text-2xl mr-2">💰</span>
                      <span className="text-sm font-medium text-gray-700">Total recargas hoy</span>
                    </div>
                    <p className="text-2xl font-bold text-purple-600">${todayStats.totalAmount.toFixed(2)}</p>
                    <p className="text-xs text-gray-500">Suma de sus recargas</p>
                  </div>
                  
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <div className="flex items-center mb-2">
                      <span className="text-2xl mr-2">🎫</span>
                      <span className="text-sm font-medium text-gray-700">Total tickets hoy</span>
                    </div>
                    <p className="text-2xl font-bold text-orange-600">{todayStats.totalTickets}</p>
                    <p className="text-xs text-gray-500">Tickets en sus recargas</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Vendor Info Card */}
            <div className="bg-white rounded-lg shadow-md mb-6">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <h2 className="text-2xl font-bold text-gray-800">{vendor.name}</h2>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${
                    vendor.status === 'active' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {vendor.status === 'active' ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div>
                    <p className="text-sm text-gray-500">Identificación</p>
                    <p className="font-medium text-gray-900">{vendor.identificationNumber}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Zona</p>
                    <p className="font-medium text-gray-900">{vendor.zone}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Fecha de registro</p>
                    <p className="font-medium text-gray-900">{formatTimestamp(vendor.createdAt)}</p>
                  </div>
                </div>
                
                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-500">Saldo disponible</p>
                    <p className="text-3xl font-bold text-blue-600">${vendor.balance || 0}</p>
                    {vendor.lastRecharge && (
                      <p className="text-xs text-gray-500 mt-1">
                        Última recarga: ${vendor.lastRecharge.amount} el {formatTimestamp(vendor.lastRecharge.date)}
                      </p>
                    )}
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-500">Tickets vendidos</p>
                    <p className="text-3xl font-bold text-green-600">{vendor.ticketsSold}</p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-500">Ingresos generados</p>
                    <p className="text-3xl font-bold text-purple-600">${vendor.revenueGenerated}</p>
                    <p className="text-sm text-gray-500">Comisión: ${vendor.commission}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Tickets Sold Statistics (Last 30 Days) */}
            <div className="bg-white rounded-lg shadow-md mb-6">
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">
                  Tickets Vendidos - Últimos 30 días
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Estadísticas de tickets vendidos desde la colección 'tickets'
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-indigo-50 p-4 rounded-lg">
                    <div className="flex items-center mb-2">
                      <span className="text-2xl mr-2">🎫</span>
                      <span className="text-sm font-medium text-gray-700">Total Tickets</span>
                    </div>
                    <p className="text-2xl font-bold text-indigo-600">{ticketsStats.last30Days}</p>
                    <p className="text-xs text-gray-500">Últimos 30 días</p>
                  </div>
                  
                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <div className="flex items-center mb-2">
                      <span className="text-2xl mr-2">🏍️</span>
                      <span className="text-sm font-medium text-gray-700">Tickets Moto</span>
                    </div>
                    <p className="text-2xl font-bold text-yellow-600">{ticketsStats.motoTickets}</p>
                    <p className="text-xs text-gray-500">Motocicletas</p>
                  </div>
                  
                  <div className="bg-teal-50 p-4 rounded-lg">
                    <div className="flex items-center mb-2">
                      <span className="text-2xl mr-2">🚗</span>
                      <span className="text-sm font-medium text-gray-700">Tickets Auto</span>
                    </div>
                    <p className="text-2xl font-bold text-teal-600">{ticketsStats.autoTickets}</p>
                    <p className="text-xs text-gray-500">Automóviles</p>
                  </div>
                  
                  <div className="bg-pink-50 p-4 rounded-lg">
                    <div className="flex items-center mb-2">
                      <span className="text-2xl mr-2">💰</span>
                      <span className="text-sm font-medium text-gray-700">Ingresos Total</span>
                    </div>
                    <p className="text-2xl font-bold text-pink-600">${ticketsStats.totalRevenue.toFixed(2)}</p>
                    <p className="text-xs text-gray-500">Valor de tickets</p>
                  </div>
                </div>
                
                {/* Recent Tickets Table */}
                {ticketsSold.length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-lg font-medium text-gray-800 mb-3">Tickets Recientes</h4>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Placa</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Zona</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Precio</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {ticketsSold.slice(0, 10).map((ticket) => (
                            <tr key={ticket.id} className="hover:bg-gray-50">
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                {ticket.formattedDate}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                                {ticket.plate}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                {ticket.vehicleType === 'Motocicleta' || ticket.vehicleType === 'moto' ? '🏍️ Moto' : '🚗 Auto'}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                {ticket.zone || ticket.zona || 'Zona no disponible'}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                                ${ticket.price?.toFixed(2)}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap">
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                  ticket.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {ticket.status === 'active' ? 'Activo' : 'Finalizado'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {ticketsSold.length > 10 && (
                      <p className="text-sm text-gray-500 mt-2 text-center">
                        Mostrando los 10 tickets más recientes de {ticketsSold.length} total
                      </p>
                    )}
                  </div>
                )}
                
                {ticketsSold.length === 0 && (
                  <div className="mt-6 text-center py-8">
                    <span className="text-4xl">📊</span>
                    <p className="text-gray-500 mt-2">No hay tickets vendidos en los últimos 30 días</p>
                  </div>
                )}
              </div>
            </div>

            {/* Recharge Form */}
            {userRole !== 'Auditor' && (
            <div className="bg-white rounded-lg shadow-md mb-6">
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Recargar Saldo</h3>
                <form onSubmit={handleRecharge}>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="rechargeAmount">
                        Monto a Recargar ($)
                      </label>
                      <input
                        id="rechargeAmount"
                        type="number"
                        value={rechargeAmount}
                        onChange={(e) => setRechargeAmount(e.target.value)}
                        className="w-full border border-gray-300 px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min="1"
                        step="0.01"
                        required
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="rechargeNotes">
                        Notas (Opcional)
                      </label>
                      <input
                        id="rechargeNotes"
                        type="text"
                        className="w-full border border-gray-300 px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Ej: Recarga semanal"
                      />
                    </div>
                  </div>
                  <div className="mt-4 flex justify-end">
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md"
                      disabled={loading}
                    >
                      {loading ? 'Procesando...' : 'Realizar Recarga'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
            )}

            {/* Recharge History */}
            <div className="bg-white rounded-lg shadow-md">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-xl font-semibold text-gray-800">Historial de Recargas</h3>
                <p className="text-sm text-gray-600">{recharges.length} registros (histórico completo)</p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha y Hora</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CA ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CA</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valor</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ejecutado por</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {recharges.map((recharge) => {
                      const isToday = getTodayRecharges().some(tr => tr.id === recharge.id);
                      return (
                        <tr key={recharge.id} className={`hover:bg-gray-50 ${isToday ? 'bg-blue-50' : ''}`}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {recharge.timestamp}
                            {isToday && <span className="ml-2 text-xs bg-blue-200 text-blue-800 px-2 py-1 rounded">HOY</span>}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {recharge.type || 'Tickets'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {recharge.caId || recharge.vendorId}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {recharge.caName || recharge.vendorName || vendor.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex flex-col">
                              <span className="text-gray-900">
                                {recharge.ticketCount} tickets {recharge.type === 'motos' || recharge.type === 'Tickets' ? '🏍️' : '🚗'}
                              </span>
                              <span className="text-green-600 font-bold">${recharge.amount}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {recharge.adminName || recharge.executedBy}
                          </td>
                        </tr>
                      );
                    })}
                    {recharges.length === 0 && (
                      <tr>
                        <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                          No hay recargas registradas para este vendedor
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirmModal.isOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-md w-full max-w-md mx-auto p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">{confirmModal.title}</h3>
            <p className="text-gray-700 mb-4">{confirmModal.message}</p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setConfirmModal(prev => ({...prev, isOpen: false}))}
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-md"
              >
                Cancelar
              </button>
              <button
                onClick={confirmModal.onConfirm}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      {alertModal.isOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-md w-full max-w-md mx-auto p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">{alertModal.title}</h3>
            <p className="text-gray-700 mb-4">{alertModal.message}</p>
            <div className="flex justify-end">
              <button
                onClick={() => setAlertModal(prev => ({...prev, isOpen: false}))}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md"
              >
                Aceptar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VendorDetails;
